import { useState } from 'react'
import styled from 'styled-components'

/** A date as YYYY-MM-DD in local time (the value of an <input type="date">). */
export const localIsoDate = (d: Date = new Date()): string =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

const addDays = (iso: string, days: number): string => {
    const d = new Date(`${iso}T12:00:00`)
    d.setDate(d.getDate() + days)
    return localIsoDate(d)
}

const Dates = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.75rem;
  color: #666;
  flex-wrap: wrap;
`

const DateInput = styled.input`
  width: 124px;
  font-size: 0.75rem;
  padding: 2px 4px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
`

const Warning = styled.span`
  color: #BF360C;
  font-size: 0.7rem;
`

interface Props {
    /** The member's name, for the inputs' accessible labels. */
    name: string
    start?: string
    end?: string
    onChange: (start: string | undefined, end: string | undefined) => void
}

/**
 * A member's own dates in their team row.
 *
 * An empty start is the opportunity's (plus the role's deferred start), filled
 * in when the opportunity is saved. The end must be after today and after the
 * start: an earlier end would take the person off the live team, which is
 * what the × does (it closes the assignment today and keeps its history).
 */
export const TeamMemberDates = ({ name, start, end, onChange }: Props) => {
    const [warning, setWarning] = useState<string | null>(null)
    const tomorrow = addDays(localIsoDate(), 1)
    const minEnd = start && start > tomorrow ? start : tomorrow
    return (
        <Dates>
            <DateInput
                type="date"
                value={start || ''}
                max={end || undefined}
                aria-label={`Desde (${name})`}
                title="Desde: primer día de esta persona en el equipo (vacío: el del proyecto)"
                onChange={(e) => {
                    const value = e.target.value || undefined
                    if (value && end && value > end) {
                        setWarning('La fecha de inicio es posterior a la de fin')
                        return
                    }
                    setWarning(null)
                    onChange(value, end)
                }}
            />
            <span aria-hidden>→</span>
            <DateInput
                type="date"
                value={end || ''}
                min={minEnd}
                aria-label={`Hasta (${name})`}
                title="Hasta: último día de esta persona en el equipo (vacío: sin fecha de fin)"
                onChange={(e) => {
                    const value = e.target.value || undefined
                    if (value && value < minEnd) {
                        setWarning(start && value < start
                            ? 'La fecha de fin es anterior a la de inicio'
                            : 'La fecha de fin debe ser posterior a hoy; para sacar a alguien del equipo usa ×')
                        return
                    }
                    setWarning(null)
                    onChange(start, value)
                }}
            />
            {warning && <Warning role="alert">{warning}</Warning>}
        </Dates>
    )
}

export default TeamMemberDates
